<template>
  <div class="d-flex flex-column flex-column-fluid">
    <!-- شريط الأدوات -->
    <div id="kt_app_toolbar" class="app-toolbar py-3 py-lg-6">
      <div
        id="kt_app_toolbar_container"
        class="app-container container-fluid d-flex flex-stack"
      >
        <div
          class="page-title d-flex flex-column justify-content-center flex-wrap me-3"
        >
          <h1 class="page-heading text-dark fw-bold fs-3 my-0">
            🎓 {{ $t("sch.list.title") || "كل المنح" }}
          </h1>
          <ul
            class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1"
          >
            <li class="breadcrumb-item text-muted">
              <router-link to="/" class="text-muted text-hover-primary">
                {{ $t("common.home") || "الصفحة الرئيسية" }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">
              {{ $t("sch.list.breadcrumb") || "المنح الدراسية" }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- المحتوى -->
    <div id="kt_app_content" class="app-content flex-column-fluid">
      <div id="kt_app_content_container" class="app-container container-fluid">
        <!-- فلاتر البحث -->
        <div class="card mb-5 p-2 shadow-sm border-0">
          <!-- عنوان الفلاتر وزرار البحث -->
          <div
            class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"
          >
            <h5 class="fw-bold text-dark mb-0">
              <i class="fa fa-search text-success me-2"></i>
              {{ $t("sch.list.filters_title") || "فلاتر البحث" }}
            </h5>

            <div class="d-flex gap-2 flex-wrap">
              <router-link
                to="/scholarships-favorites"
                class="btn btn-outline-danger fw-bold btn-sm"
              >
                <i class="fa fa-heart me-1"></i>
                {{ $t("favorites.title") || "المفضلة" }}
              </router-link>
              <button
                class="btn btn-outline-success fw-bold btn-sm"
                @click="toggleBasicFilters"
              >
                <i
                  :class="[
                    'fa',
                    showAdvanced ? 'fa-chevron-up' : 'fa-sliders-h',
                  ]"
                  class="me-1"
                ></i>
                {{
                  showAdvanced
                    ? $t("common.hide_basic") || "إخفاء البحث الأساسي"
                    : $t("common.basic_search") || "بحث أساسي"
                }}
              </button>
              <button
                class="btn btn-outline-success fw-bold btn-sm"
                @click="toggleAdvancedFilters"
              >
                <i
                  :class="[
                    'fa',
                    showBasicSearch ? 'fa-chevron-up' : 'fa-sliders-h',
                  ]"
                  class="me-1"
                ></i>
                {{
                  showBasicSearch
                    ? $t("common.hide_advanced") || "إخفاء البحث المتقدم"
                    : $t("common.advanced_search") || "بحث متقدم"
                }}
              </button>
            </div>
          </div>

          <!-- الفلاتر الأساسية -->
          <transition name="fade">
            <div class="row g-3 m-2" v-if="showAdvanced">
              <!-- البحث النصي -->
              <div class="col-12 col-md-4">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.search") || "بحث"
                }}</label>
                <input
                  v-model="filters.q"
                  type="text"
                  class="form-control form-control-sm shadow-sm"
                  :placeholder="
                    $t('sch.list.search_placeholder') ||
                    'ابحث باسم المنحة أو الكود أو المدينة أو السنة'
                  "
                  @input="applyFilters"
                />
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.country") || "الدولة"
                }}</label>
                <select
                  v-model="filters.country_id"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">
                    {{ $t("sch.list.all_countries") || "كل الدول" }}
                  </option>
                  <option
                    v-for="c in metadata.countries"
                    :key="c.id"
                    :value="c.id"
                  >
                    {{ c.name }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.level") || "المستوى"
                }}</label>
                <select
                  v-model="filters.level"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") || "الكل" }}</option>
                  <option
                    v-for="lvl in metadata.levels"
                    :key="lvl.value || lvl"
                    :value="lvl.value || lvl"
                  >
                    {{ lvl.label || lvl }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.type") || "نوع المنحة"
                }}</label>
                <select
                  v-model="filters.type"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="t in metadata.types"
                    :key="t.value || t"
                    :value="t.value || t"
                  >
                    {{ t.label || t }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.study_mode") || "نمط الدراسة"
                }}</label>
                <select
                  v-model="filters.study_mode"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="m in metadata.study_modes"
                    :key="m.value || m"
                    :value="m.value || m"
                  >
                    {{ m.label || m }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.funding") || "جهة التمويل"
                }}</label>
                <select
                  v-model="filters.funding_type"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="f in metadata.funding_types"
                    :key="f.value || f"
                    :value="f.value || f"
                  >
                    {{ f.label || f }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.sponsor") || "الجهة الراعية"
                }}</label>
                <select
                  v-model="filters.sponsor_id"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="s in metadata.sponsors"
                    :key="s.id"
                    :value="s.id"
                  >
                    {{ s.name }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.host_institution") || "الجهة المستضيفة"
                }}</label>
                <select
                  v-model="filters.host_institution_id"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="h in metadata.host_institutions"
                    :key="h.id"
                    :value="h.id"
                  >
                    {{ h.name_ar || h.name }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.program_language") || "لغة البرنامج"
                }}</label>
                <select
                  v-model="filters.program_language"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="lang in metadata.program_languages"
                    :key="lang"
                    :value="lang"
                  >
                    {{ lang }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.status") || "الحالة"
                }}</label>
                <select
                  v-model="filters.status"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="s in metadata.statuses"
                    :key="s.value || s"
                    :value="s.value || s"
                  >
                    {{ s.label || s }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.field") || "التخصص"
                }}</label>
                <select
                  v-model="filters.field_id"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="f in metadata.fields"
                    :key="f.id"
                    :value="f.id"
                  >
                    {{ f.name }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.program_year") || "السنة الأكاديمية"
                }}</label>
                <select
                  v-model="filters.program_year"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") }}</option>
                  <option
                    v-for="y in metadata.program_years"
                    :key="y"
                    :value="y"
                  >
                    {{ y }}
                  </option>
                </select>
              </div>

              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.open_state") || "حالة الفتح"
                }}</label>
                <select
                  v-model="filters.open_only"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">{{ $t("common.all") || "الكل" }}</option>
                  <option :value="true">
                    {{ $t("sch.list.open") || "مفتوحة" }}
                  </option>
                  <option :value="false">
                    {{ $t("sch.list.closed") || "مغلقة" }}
                  </option>
                </select>
              </div>
              <!-- الترتيب -->
              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.sort_by") || "الترتيب حسب"
                }}</label>
                <select
                  v-model="filters.sort_by"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">
                    {{ $t("common.select") || "اختر..." }}
                  </option>
                  <option value="created_at">created_at</option>
                  <option value="application_deadline">
                    application_deadline
                  </option>
                  <option value="name_original">name_original</option>
                  <option value="program_year">program_year</option>
                  <option value="duration_months">duration_months</option>
                  <option value="seats_available">seats_available</option>
                  <option value="required_gpa">required_gpa</option>
                  <option value="application_open">application_open</option>
                  <option value="start_date">start_date</option>
                </select>
              </div>
              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("sch.list.sort_order") || "اتجاه الترتيب"
                }}</label>
                <select
                  v-model="filters.sort_order"
                  class="form-select form-select-sm shadow-sm"
                  @change="applyFilters"
                >
                  <option value="">
                    {{ $t("common.select") || "اختر..." }}
                  </option>
                  <option value="asc">asc</option>
                  <option value="desc">desc</option>
                </select>
              </div>

              <!-- عناصر الصفحة -->
              <div class="col-6 col-md-3">
                <label class="form-label fw-bold small">{{
                  $t("common.per_page") || "عدد العناصر في الصفحة"
                }}</label>
                <input
                  type="number"
                  class="form-control form-control-sm shadow-sm"
                  v-model="filters.per_page"
                  @change="applyFilters"
                />
              </div>
            </div>
          </transition>
          <!-- الفلاتر المتقدمة -->
          <transition name="fade">
            <div v-if="showBasicSearch" class="advanced-filters mt-3">
              <div class="card border-0 shadow-sm mb-3 p-3 rounded-4">
                <h6 class="fw-bold text-success mb-3">
                  <i class="fa fa-sliders-h me-2 text-success"></i>
                  {{ $t("common.advanced_search") || "بحث متقدم" }}
                </h6>
                <div class="row g-3 m-1">
                  <!-- مدة البرنامج -->
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.min_duration") || "المدة الدنيا (أشهر)"
                    }}</label>
                    <input
                      type="number"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.min_duration"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.max_duration") || "المدة القصوى (أشهر)"
                    }}</label>
                    <input
                      type="number"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.max_duration"
                      @change="applyFilters"
                    />
                  </div>

                  <!-- تواريخ -->
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.deadline_after") || "آخر موعد بعد"
                    }}</label>
                    <input
                      type="date"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.deadline_after"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.deadline_before") || "آخر موعد قبل"
                    }}</label>
                    <input
                      type="date"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.deadline_before"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.start_date_from") || "بداية البرنامج من"
                    }}</label>
                    <input
                      type="date"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.start_date_from"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.start_date_to") || "بداية البرنامج إلى"
                    }}</label>
                    <input
                      type="date"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.start_date_to"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.results_from") || "إعلان النتائج من"
                    }}</label>
                    <input
                      type="date"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.results_from"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.results_to") || "إعلان النتائج إلى"
                    }}</label>
                    <input
                      type="date"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.results_to"
                      @change="applyFilters"
                    />
                  </div>

                  <!-- أكاديمي ومتطلبات -->
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.min_gpa") || "أدنى معدل (GPA)"
                    }}</label>
                    <input
                      type="number"
                      step="0.01"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.min_gpa"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.max_gpa") || "أقصى معدل (GPA)"
                    }}</label>
                    <input
                      type="number"
                      step="0.01"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.max_gpa"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.max_age") || "أقصى عمر"
                    }}</label>
                    <input
                      type="number"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.max_age"
                      @change="applyFilters"
                    />
                  </div>

                  <!-- منصة التقديم ولغة التقديم -->

                  <div class="col-6 col-md-3 d-flex align-items-end">
                    <div class="form-check form-switch">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        id="free_application"
                        v-model="filters.free_application"
                        @change="applyFilters"
                      />
                      <label
                        class="form-check-label fw-semibold small ms-1"
                        for="free_application"
                        >{{
                          $t("sch.list.free_application") || "تقديم مجاني فقط"
                        }}</label
                      >
                    </div>
                  </div>

                  <!-- معلومات عامة إضافية / المقاعد -->

                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.min_seats") || "أدنى مقاعد"
                    }}</label>
                    <input
                      type="number"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.min_seats"
                      @change="applyFilters"
                    />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label fw-bold small">{{
                      $t("sch.list.max_seats") || "أقصى مقاعد"
                    }}</label>
                    <input
                      type="number"
                      class="form-control form-control-sm shadow-sm"
                      v-model="filters.max_seats"
                      @change="applyFilters"
                    />
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- تحميل -->
        <div
          v-if="isLoading"
          class="loader text-success text-center p-5"
          style="font-weight: 800; border-radius: 10px; background: #73737326"
        >
          {{ $t("sch.list.loading") || "جاري تحميل المنح..." }}
          <div class="spinner-border text-success mt-2" role="status"></div>
        </div>

        <!-- عرض المنح -->
        <div v-else>
          <div v-if="scholarships.length" class="row g-4">
            <div
              v-for="scholarship in scholarships"
              :key="scholarship.id"
              class="col-md-6 col-lg-4 col-xl-3"
            >
              <div
                class="card scholarship-card h-100 shadow-sm"
                @click="goToScholarship(scholarship.id)"
              >
                <img
                  :src="getBannerImage(scholarship)"
                  class="card-img-top scholarship-img"
                  alt="صورة المنحة"
                  @error="handleImageError"
                />
                <div class="card-body">
                  <h5 class="card-title fw-bold text-dark mb-2">
                    {{
                      scholarship.name_ar ||
                      scholarship.name ||
                      scholarship.name_original
                    }}
                  </h5>

                  <!-- الراعي -->
                  <p
                    v-if="getSponsorName(scholarship)"
                    class="card-text text-muted small mb-1"
                  >
                    <i class="fa fa-handshake text-success me-1"></i>
                    {{ getSponsorName(scholarship) }}
                  </p>

                  <!-- المستويات -->
                  <div v-if="getLevelsText(scholarship)" class="mb-2">
                    <span
                      class="badge-level me-1"
                      v-for="lvl in getLevelsText(scholarship).split('\n')"
                      :key="lvl"
                      >{{ lvl }}</span
                    >
                  </div>

                  <!-- التواريخ المهمة -->
                  <div class="d-flex flex-column gap-1 small text-muted">
                    <div v-if="scholarship.dates?.application_deadline">
                      <i class="fa fa-hourglass-end text-success me-1"></i>
                      {{
                        $t("sch.details.labels.application_deadline") ||
                        "آخر موعد للتقديم"
                      }}:
                      <span class="fw-semibold">{{
                        formatDate(scholarship.dates.application_deadline)
                      }}</span>
                    </div>
                    <div v-if="scholarship.dates?.start_date">
                      <i class="fa fa-calendar-day text-success me-1"></i>
                      {{
                        $t("sch.details.labels.start_date") || "تاريخ البدء"
                      }}:
                      <span class="fw-semibold">{{
                        formatDate(scholarship.dates.start_date)
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- لا توجد نتائج -->
          <div v-else class="alert alert-warning text-center mt-5">
            {{ $t("sch.list.empty") || "لا توجد منح مطابقة لمرشحات البحث." }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";

export default {
  name: "AllScholarships",
  data() {
    return {
      scholarships: [],
      metadata: {
        countries: [],
        sponsors: [],
        host_institutions: [],
        fields: [],
        types: [],
        levels: [],
        funding_types: [],
        study_modes: [],
        statuses: [],
        program_languages: [],
        program_years: [],
      },
      filters: {
        q: "",
        country_id: "",
        sponsor_id: "",
        level: "",
        type: "",
        funding_type: "",
        status: "",
        study_mode: "",
        host_institution_id: "",
        program_language: "",
        field_id: "",
        program_year: "",
        open_only: "",
        // durations
        min_duration: "",
        max_duration: "",
        // dates
        deadline_after: "",
        deadline_before: "",
        start_date_from: "",
        start_date_to: "",
        results_from: "",
        results_to: "",
        // application state
        accepting_applications: false,
        // academic
        min_gpa: "",
        max_gpa: "",
        max_age: "",
        language_requirement: "",
        academic_status: "",
        admission_requirement: "",
        // application platform
        application_platform_id: "",
        application_language: "",
        free_application: false,
        // general
        call_code: "",
        city: "",
        // availability
        min_seats: "",
        max_seats: "",
        // nationality and fields
        nationality_id: "",
        nationality_ids: "",
        field_ids: "",
        // subcategories
        subcategory_id: "",
        subcategory_ids: "",
        // other flags
        return_commitment: false,
        reporting_required: false,
        has_work_restrictions: "",
        tuition_covered: false,
        // sorting & pagination
        sort_by: "",
        sort_order: "",
        per_page: "",
      },
      showAdvanced: false,
      showBasicSearch: false,
      isLoading: true,
      defaultImage: "/assets/img/acad-icon.png",
    };
  },
  methods: {
    toggleBasicFilters() {
      this.showAdvanced = !this.showAdvanced;
      if (this.showAdvanced) this.showBasicSearch = false;
    },
    toggleAdvancedFilters() {
      this.showBasicSearch = !this.showBasicSearch;
      if (this.showBasicSearch) this.showAdvanced = false;
    },
    formatDate(iso) {
      try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString();
      } catch (e) {
        return "";
      }
    },
    getSponsorName(s) {
      if (!s) return "";
      if (s.sponsor && (s.sponsor.name_ar || s.sponsor.name))
        return s.sponsor.name_ar || s.sponsor.name;
      if (s.sponsor_name) return s.sponsor_name;
      return "";
    },
    getLevelsText(s) {
      if (!s || !s.levels || !Array.isArray(s.levels) || !s.levels.length)
        return "";
      // Keep as provided; backend may already localize labels
      return s.levels.join("\n");
    },
    async fetchMetadata() {
      try {
        const res = await fetch(`${apiUrl}/scholarships/filters/metadata`);
        const data = await res.json();
        this.metadata = data.data;
      } catch (error) {
        console.error("خطأ في تحميل بيانات الفلاتر:", error);
      }
    },
    async fetchScholarships() {
      this.isLoading = true;
      try {
        const query = new URLSearchParams();

        for (const key in this.filters) {
          const value = this.filters[key];
          if (value === "" || value === null || value === undefined) continue;
          if (typeof value === "boolean" && value === false) continue;
          query.append(key, value);
        }

        const url = `${apiUrl}/scholarships?${query.toString()}`;
        const res = await fetch(url);
        const data = await res.json();
        this.scholarships = data.data || [];
      } catch (error) {
        console.error("خطأ في تحميل المنح:", error);
      } finally {
        this.isLoading = false;
      }
    },
    applyFilters() {
      this.fetchScholarships();
    },
    resetFilters() {
      Object.keys(this.filters).forEach((k) => (this.filters[k] = ""));
      this.fetchScholarships();
    },
    getBannerImage(scholarship) {
      if (
        scholarship.banners &&
        scholarship.banners.length &&
        scholarship.banners[0].image_url
      ) {
        return scholarship.banners[0].image_url;
      }
      if (scholarship.image) {
        return scholarship.image.startsWith("http")
          ? scholarship.image
          : `${apiUrl}${scholarship.image}`;
      }
      return this.defaultImage;
    },
    handleImageError(e) {
      e.target.src = this.defaultImage;
    },
    goToScholarship(id) {
      window.location.href = `/scholarship-details?id=${id}`;
    },
  },
  async mounted() {
    await this.fetchMetadata();
    await this.fetchScholarships();
  },
};
</script>

<style scoped>
/* ========== Main Card Styles ========== */
.scholarship-card {
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border-radius: 20px !important;
  border: 1px solid rgba(0, 196, 68, 0.08) !important;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  overflow: hidden;
  position: relative;
}
.scholarship-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #00c444, #00e050, #00c444);
  background-size: 200% 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;
}
.scholarship-card:hover::before {
  opacity: 1;
  animation: gradientSlide 2s linear infinite;
}
@keyframes gradientSlide {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}
.scholarship-card:hover {
  transform: translateY(-12px) scale(1.02);
  box-shadow: 0 25px 50px rgba(0, 196, 68, 0.18),
    0 10px 20px rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(0, 196, 68, 0.2) !important;
}

/* ========== Image Styles ========== */
.scholarship-img {
  height: 200px;
  object-fit: cover;
  border-radius: 20px 20px 0 0 !important;
  transition: all 0.5s ease;
  position: relative;
}
.scholarship-card:hover .scholarship-img {
  transform: scale(1.08);
  filter: brightness(1.05);
}
.scholarship-card .card-img-top {
  overflow: hidden;
}

/* ========== Card Body ========== */
.scholarship-card .card-body {
  padding: 20px;
  position: relative;
}
.scholarship-card .card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.5;
  margin-bottom: 12px;
  transition: color 0.3s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.scholarship-card:hover .card-title {
  color: #00a43c;
}
.scholarship-card .card-text {
  color: #666;
  font-size: 0.85rem;
}

/* ========== Badge Levels ========== */
.badge-level {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%);
  color: #0ea35f;
  border: 1px solid rgba(0, 196, 68, 0.25);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 0.72rem;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 4px;
}
.badge-level:hover {
  background: linear-gradient(135deg, #d4f7e2 0%, #c6ffda 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.2);
}

/* ========== Filter Card ========== */
.card.mb-5.p-2 {
  border-radius: 24px !important;
  background: linear-gradient(145deg, #ffffff 0%, #f8fffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.1) !important;
  box-shadow: 0 8px 32px rgba(0, 196, 68, 0.08) !important;
  padding: 20px !important;
  position: relative;
  overflow: hidden;
}
.card.mb-5.p-2::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 200px;
  height: 200px;
  background: radial-gradient(
    circle,
    rgba(0, 196, 68, 0.05) 0%,
    transparent 70%
  );
  border-radius: 50%;
  transform: translate(30%, -30%);
  pointer-events: none;
}

/* ========== Buttons ========== */
.btn-outline-success {
  border: 2px solid #00c444 !important;
  color: #00c444 !important;
  background: transparent;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.btn-outline-success::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 196, 68, 0.1),
    transparent
  );
  transition: left 0.5s ease;
}
.btn-outline-success:hover::before {
  left: 100%;
}
.btn-outline-success:hover {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  color: #fff !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.3);
  border-color: transparent !important;
}
.btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  border: none !important;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
  transition: all 0.3s ease;
}
.btn-success:hover {
  background: linear-gradient(135deg, #00d44a 0%, #00b844 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.4);
}
.text-success {
  color: #00c444 !important;
}

/* ========== Form Controls ========== */
.form-select,
.form-control {
  border-radius: 12px;
  border: 1.5px solid #e8f5ed;
  padding: 10px 14px;
  transition: all 0.3s ease;
  background-color: #fafffe;
}
.form-select:focus,
.form-control:focus {
  border-color: #00c444;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.12);
  background-color: #fff;
}
.form-select:hover,
.form-control:hover {
  border-color: rgba(0, 196, 68, 0.4);
}
.form-label {
  color: #2d3748;
  font-weight: 600;
  margin-bottom: 6px;
}
.form-check-input:checked {
  background-color: #00c444 !important;
  border-color: #00c444 !important;
  box-shadow: 0 2px 8px rgba(0, 196, 68, 0.3);
}

/* ========== Transitions ========== */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ========== Advanced Filters ========== */
.advanced-filters .card {
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%);
  border: none !important;
  border-radius: 20px !important;
  border-left: 4px solid #00c444 !important;
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.08);
}
.advanced-filters h6 {
  font-size: 1rem;
}

/* ========== Loader ========== */
.loader {
  border-radius: 20px !important;
  background: linear-gradient(145deg, #f8fffe 0%, #f0fff5 100%) !important;
  border: 1px solid rgba(0, 196, 68, 0.1);
  animation: loaderPulse 1.5s ease-in-out infinite;
}
@keyframes loaderPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* ========== Alert ========== */
.alert-warning {
  border-radius: 16px;
  border: none;
  background: linear-gradient(135deg, #fff8e6 0%, #fff3d4 100%);
  box-shadow: 0 4px 20px rgba(255, 193, 7, 0.15);
}

/* ========== Page Title ========== */
.page-heading {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ========== Grid Animation ========== */
.row.g-4 > div {
  animation: cardFadeIn 0.5s ease forwards;
  opacity: 0;
}
.row.g-4 > div:nth-child(1) {
  animation-delay: 0.05s;
}
.row.g-4 > div:nth-child(2) {
  animation-delay: 0.1s;
}
.row.g-4 > div:nth-child(3) {
  animation-delay: 0.15s;
}
.row.g-4 > div:nth-child(4) {
  animation-delay: 0.2s;
}
.row.g-4 > div:nth-child(5) {
  animation-delay: 0.25s;
}
.row.g-4 > div:nth-child(6) {
  animation-delay: 0.3s;
}
.row.g-4 > div:nth-child(7) {
  animation-delay: 0.35s;
}
.row.g-4 > div:nth-child(8) {
  animation-delay: 0.4s;
}
@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== Date Info Styling ========== */
.scholarship-card .d-flex.flex-column.gap-1 {
  background: linear-gradient(135deg, #f8fffe 0%, #f0fff5 100%);
  border-radius: 12px;
  padding: 10px 12px;
  margin-top: 12px;
  border: 1px solid rgba(0, 196, 68, 0.1);
}
.scholarship-card .d-flex.flex-column.gap-1 i {
  width: 18px;
  text-align: center;
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .advanced-filters .card {
    padding: 1rem !important;
  }
  .form-label {
    font-size: 0.85rem;
  }
  .scholarship-card {
    border-radius: 16px !important;
  }
  .scholarship-img {
    height: 160px;
  }
  .card.mb-5.p-2 {
    padding: 16px !important;
    border-radius: 18px !important;
  }
}

/* ========== Scrollbar ========== */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: #f0fff5;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #00c444 0%, #00a43c 100%);
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #00d44a 0%, #00b844 100%);
}

/* ========== DARK MODE ========== */
[data-bs-theme="dark"] .scholarship-card,
[data-theme="dark"] .scholarship-card,
.dark .scholarship-card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 196, 68, 0.05);
}
[data-bs-theme="dark"] .scholarship-card:hover,
[data-theme="dark"] .scholarship-card:hover,
.dark .scholarship-card:hover {
  box-shadow: 0 25px 50px rgba(0, 196, 68, 0.2), 0 10px 20px rgba(0, 0, 0, 0.4) !important;
  border-color: rgba(0, 196, 68, 0.3) !important;
}

[data-bs-theme="dark"] .scholarship-card .card-title,
[data-theme="dark"] .scholarship-card .card-title,
.dark .scholarship-card .card-title {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .scholarship-card:hover .card-title,
[data-theme="dark"] .scholarship-card:hover .card-title,
.dark .scholarship-card:hover .card-title {
  color: #00e050;
}

[data-bs-theme="dark"] .scholarship-card .card-text,
[data-theme="dark"] .scholarship-card .card-text,
.dark .scholarship-card .card-text {
  color: #b0b3b8;
}

[data-bs-theme="dark"] .scholarship-card .card-body,
[data-theme="dark"] .scholarship-card .card-body,
.dark .scholarship-card .card-body {
  background: transparent;
}

[data-bs-theme="dark"] .badge-level,
[data-theme="dark"] .badge-level,
.dark .badge-level {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  color: #8bffb3;
  border-color: rgba(0, 196, 68, 0.35);
}
[data-bs-theme="dark"] .badge-level:hover,
[data-theme="dark"] .badge-level:hover,
.dark .badge-level:hover {
  background: linear-gradient(135deg, #1e4d35 0%, #237a4d 100%);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.25);
}

/* Filter Card Dark Mode */
[data-bs-theme="dark"] .card.mb-5.p-2,
[data-theme="dark"] .card.mb-5.p-2,
.dark .card.mb-5.p-2 {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4) !important;
}
[data-bs-theme="dark"] .card.mb-5.p-2::before,
[data-theme="dark"] .card.mb-5.p-2::before,
.dark .card.mb-5.p-2::before {
  background: radial-gradient(
    circle,
    rgba(0, 196, 68, 0.1) 0%,
    transparent 70%
  );
}

[data-bs-theme="dark"] .card.mb-5.p-2 h5,
[data-theme="dark"] .card.mb-5.p-2 h5,
.dark .card.mb-5.p-2 h5 {
  color: #e4e6eb;
}

/* Form Controls Dark Mode */
[data-bs-theme="dark"] .form-select,
[data-bs-theme="dark"] .form-control,
[data-bs-theme="dark"] input[type="text"],
[data-bs-theme="dark"] input[type="number"],
[data-bs-theme="dark"] input[type="date"],
[data-bs-theme="dark"] select,
[data-theme="dark"] .form-select,
[data-theme="dark"] .form-control,
[data-theme="dark"] input[type="text"],
[data-theme="dark"] input[type="number"],
[data-theme="dark"] input[type="date"],
[data-theme="dark"] select,
.dark .form-select,
.dark .form-control,
.dark input[type="text"],
.dark input[type="number"],
.dark input[type="date"],
.dark select {
  background-color: #3a3b3d !important;
  border-color: rgba(0, 196, 68, 0.2) !important;
  color: #e4e6eb !important;
}
[data-bs-theme="dark"] .form-select:focus,
[data-bs-theme="dark"] .form-control:focus,
[data-bs-theme="dark"] input:focus,
[data-bs-theme="dark"] select:focus,
[data-theme="dark"] .form-select:focus,
[data-theme="dark"] .form-control:focus,
[data-theme="dark"] input:focus,
[data-theme="dark"] select:focus,
.dark .form-select:focus,
.dark .form-control:focus,
.dark input:focus,
.dark select:focus {
  background-color: #3a3b3d !important;
  border-color: #00c444 !important;
  color: #e4e6eb !important;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.15) !important;
}
[data-bs-theme="dark"] .form-select:hover,
[data-bs-theme="dark"] .form-control:hover,
[data-bs-theme="dark"] input:hover,
[data-bs-theme="dark"] select:hover,
[data-theme="dark"] .form-select:hover,
[data-theme="dark"] .form-control:hover,
[data-theme="dark"] input:hover,
[data-theme="dark"] select:hover,
.dark .form-select:hover,
.dark .form-control:hover,
.dark input:hover,
.dark select:hover {
  border-color: rgba(0, 196, 68, 0.5) !important;
}

/* Form Select Arrow Dark Mode */
[data-bs-theme="dark"] .form-select,
[data-theme="dark"] .form-select,
.dark .form-select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%2300e050' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e") !important;
}

/* Placeholder Dark Mode */
[data-bs-theme="dark"] input::placeholder,
[data-theme="dark"] input::placeholder,
.dark input::placeholder {
  color: #8b8d91 !important;
  opacity: 1;
}

[data-bs-theme="dark"] .form-label,
[data-theme="dark"] .form-label,
.dark .form-label {
  color: #b0b3b8 !important;
}

/* Form Check/Switch Dark Mode */
[data-bs-theme="dark"] .form-check-label,
[data-theme="dark"] .form-check-label,
.dark .form-check-label {
  color: #b0b3b8 !important;
}

/* Advanced Filters Dark Mode */
[data-bs-theme="dark"] .advanced-filters .card,
[data-theme="dark"] .advanced-filters .card,
.dark .advanced-filters .card {
  background: linear-gradient(145deg, #2d2e30 0%, #242526 100%);
  border-left-color: #00c444 !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
[data-bs-theme="dark"] .advanced-filters h6,
[data-theme="dark"] .advanced-filters h6,
.dark .advanced-filters h6 {
  color: #00e050;
}

/* Loader Dark Mode */
[data-bs-theme="dark"] .loader,
[data-theme="dark"] .loader,
.dark .loader {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%) !important;
  border-color: rgba(0, 196, 68, 0.15);
  color: #00e050 !important;
}

/* Alert Dark Mode */
[data-bs-theme="dark"] .alert-warning,
[data-theme="dark"] .alert-warning,
.dark .alert-warning {
  background: linear-gradient(135deg, #3d3520 0%, #4a3f1a 100%);
  color: #ffd666;
  box-shadow: 0 4px 20px rgba(255, 193, 7, 0.1);
}

/* Page Heading Dark Mode */
[data-bs-theme="dark"] .page-heading,
[data-theme="dark"] .page-heading,
.dark .page-heading {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Breadcrumb Dark Mode */
[data-bs-theme="dark"] .breadcrumb-item.text-muted,
[data-theme="dark"] .breadcrumb-item.text-muted,
.dark .breadcrumb-item.text-muted {
  color: #b0b3b8 !important;
}
[data-bs-theme="dark"] .breadcrumb-item a,
[data-theme="dark"] .breadcrumb-item a,
.dark .breadcrumb-item a {
  color: #00e050 !important;
}

/* Date Info Dark Mode */
[data-bs-theme="dark"] .scholarship-card .d-flex.flex-column.gap-1,
[data-theme="dark"] .scholarship-card .d-flex.flex-column.gap-1,
.dark .scholarship-card .d-flex.flex-column.gap-1 {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  border-color: rgba(0, 196, 68, 0.2);
}
[data-bs-theme="dark"] .scholarship-card .d-flex.flex-column.gap-1 .text-muted,
[data-theme="dark"] .scholarship-card .d-flex.flex-column.gap-1 .text-muted,
.dark .scholarship-card .d-flex.flex-column.gap-1 .text-muted {
  color: #b0b3b8 !important;
}

/* Buttons Dark Mode */
[data-bs-theme="dark"] .btn-outline-success,
[data-theme="dark"] .btn-outline-success,
.dark .btn-outline-success {
  border-color: #00e050 !important;
  color: #00e050 !important;
}
[data-bs-theme="dark"] .btn-outline-success:hover,
[data-theme="dark"] .btn-outline-success:hover,
.dark .btn-outline-success:hover {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  color: #fff !important;
  border-color: transparent !important;
}

/* Scrollbar Dark Mode */
[data-bs-theme="dark"] ::-webkit-scrollbar-track,
[data-theme="dark"] ::-webkit-scrollbar-track,
.dark ::-webkit-scrollbar-track {
  background: #242526;
}
</style>
